import { trackLoggedOutRetailersHomePageBannerInsiderMembershipButtonClick } from "@faire/web-api/events/loggedOutRetailers/click/homePageBannerInsiderMembershipButton";
import { trackLoggedOutRetailersLoggedOutHomeRedesignView } from "@faire/web-api/events/loggedOutRetailers/view/loggedOutHomeRedesign";
import insiderMarketingPage from "@faire/web-api/routes/insider";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Button } from "@faire/web/slate/Button";
import { Column, Flex } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import classNames from "classnames";
import React from "react";

import { getSettingInsiderLoggedOutMarketingPageEnabled } from "@faire/retailer/settings/getSettingInsiderLoggedOutMarketingPageEnabled";
import * as bannerStyles from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/__internal__/LoggedOutHomeBanner/banner.css";
import { WeMakeItSimpleRightImage } from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/WeMakeItSimpleBanner/__internal__/WeMakeItSimpleRightImage";

import * as componentStyles from "./styles.css";

export const WeMakeItSimpleBanner: React.FC = () => {
  const ref = useDoActionWhenVisible(() =>
    trackLoggedOutRetailersLoggedOutHomeRedesignView("we_make_it_simple_banner")
  );
  return (
    <Flex
      direction={{ mobileAndAbove: "column", tabletAndAbove: "row" }}
      className={componentStyles.container}
      data-test-id="we-make-it-simple-banner"
      ref={ref}
    >
      <div
        className={bannerStyles.halfBanner}
        style={{ height: "100%", alignContent: "center" }}
      >
        <LeftContent />
      </div>

      <div
        className={classNames(
          bannerStyles.halfBanner,
          componentStyles.imageContainer
        )}
      >
        <WeMakeItSimpleRightImage />
      </div>
    </Flex>
  );
};

const Paragraph = ({
  number,
  content,
}: {
  number: string;
  content: string | React.ReactNode;
}) => {
  return (
    <>
      <Typography
        variant={{
          mobileAndAbove: "subheadingSansMedium",
          tabletAndAbove: "paragraphSansMedium",
          desktopAndAbove: "subheadingSansMedium",
        }}
      >
        {number}
      </Typography>
      <Spacer size="1x" />
      <Typography
        variant={{
          mobileAndAbove: "subheadingSansRegular",
          tabletAndAbove: "paragraphSansRegular",
          desktopAndAbove: "subheadingSansRegular",
        }}
      >
        {content}
      </Typography>
    </>
  );
};

const LeftContent = () => {
  const { strictLocalize } = useStrictLocalization();
  return (
    <Column className={componentStyles.leftContent}>
      <Typography
        variant={{
          mobileAndAbove: "displaySSerifRegular",
          desktopAndAbove: "displayMSerifRegular",
        }}
      >
        <StrictLocalMsg
          defaultMessage="We make it simple."
          description={{
            text: "Logged out home page we make it simple banner title",
            img: "https://cdn.faire.com/fastly/6f2f577b59b1290ddafc39c29d6edad276d0b4d00d900281891384bbcea68be8.png",
          }}
        />
      </Typography>
      <Typography
        variant={{
          mobileAndAbove: "displaySSerifRegular",
          desktopAndAbove: "displayMSerifRegular",
        }}
      >
        <StrictLocalMsg
          defaultMessage="You make it yours."
          description={{
            text: "Logged out home page we make it simple banner title",
            img: "https://cdn.faire.com/fastly/6f2f577b59b1290ddafc39c29d6edad276d0b4d00d900281891384bbcea68be8.png",
          }}
        />
      </Typography>
      <Spacer size={{ mobileAndAbove: "3x", desktopAndAbove: "4x" }} />
      <Paragraph
        number="01"
        content={strictLocalize({
          defaultMessage:
            "Try new brands with confidence—you’ve got free, easy returns on all first orders.",
          description: {
            text: "Logged out home page we make it simple content",
            img: "https://cdn.faire.com/fastly/6f2f577b59b1290ddafc39c29d6edad276d0b4d00d900281891384bbcea68be8.png",
          },
        })}
      />
      <Spacer size={{ mobileAndAbove: "3x", desktopAndAbove: "4x" }} />
      <Paragraph
        number="02"
        content={strictLocalize({
          defaultMessage:
            "Buy new inventory for your shop now and pay invoices 60 days later, with zero fees.",
          description: {
            text: "Logged out home page we make it simple content",
            img: "https://cdn.faire.com/fastly/6f2f577b59b1290ddafc39c29d6edad276d0b4d00d900281891384bbcea68be8.png",
          },
        })}
      />
      <Spacer size={{ mobileAndAbove: "3x", desktopAndAbove: "4x" }} />
      <Paragraph
        number="03"
        content={
          getSettingInsiderLoggedOutMarketingPageEnabled() ? (
            <StrictLocalMsg
              defaultMessage="Unlock free shipping with thousands of brands with a Faire <LinkFormatting>Insider membership</LinkFormatting>."
              description={{
                text: "Logged out home page we make it simple content",
                img: "https://cdn.faire.com/fastly/6f2f577b59b1290ddafc39c29d6edad276d0b4d00d900281891384bbcea68be8.png",
              }}
              values={{ LinkFormatting }}
            />
          ) : (
            <StrictLocalMsg
              defaultMessage="Unlock free shipping with thousands of brands with a Faire Insider membership."
              description={{
                text: "Logged out home page we make it simple content",
                img: "https://cdn.faire.com/fastly/6f2f577b59b1290ddafc39c29d6edad276d0b4d00d900281891384bbcea68be8.png",
              }}
            />
          )
        }
      />
    </Column>
  );
};

const LinkFormatting = (chunks: JSX.Element) => (
  <Button
    variant="tertiary"
    onClick={trackLoggedOutRetailersHomePageBannerInsiderMembershipButtonClick}
    linkTo={insiderMarketingPage()}
    target="_blank"
  >
    <Typography
      variant={{
        mobileAndAbove: "subheadingSansRegular",
        tabletAndAbove: "paragraphSansRegular",
        desktopAndAbove: "subheadingSansRegular",
      }}
    >
      {chunks}
    </Typography>
  </Button>
);
